/**
 * useResumeAI.js
 *
 * Advanced AI Resume Intelligence Hook
 * - Non-repetitive summary generation
 * - Job description analysis
 * - Bullet enhancement with STAR format
 * - Dynamic personalization
 * - ATS optimization
 */

import { useState, useCallback } from 'react';
import {
  generateSummaryVariations,
  analyzeJobDescription as analyzeJD,
  enhanceBullet as enhanceBulletAI,
  generateSTARBulletFallback,
  QA_SPECIALIZATIONS,
  cleanAndEnhanceAnalysis,
} from '../utils/aiResumeEngine';

export { QA_SPECIALIZATIONS };

function simulateDelay(ms = 800) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const SESSION_KEY = 'auth_session';

function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s?.verified || !s?.email || !s?.expiresAt) return null;
    if (s.expiresAt < Date.now()) return null;
    return s;
  } catch { return null; }
}

async function callResumeAI(action, payload) {
  const session = getSession();
  
  if (localStorage.getItem('ai_limit_reached') === 'true') {
    throw new Error('LIMIT_EXCEEDED');
  }

  // We allow guests to call certain endpoints (like analyze-jd). The backend will enforce limits/auth based on the specific action.

  const response = await fetch('/api/resume-router', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-session-token': session?.token || '',
      'x-session-email': session?.email || ''
    },
    body: JSON.stringify({ action, payload })
  });

  if (response.status === 429) {
    localStorage.setItem('ai_limit_reached', 'true');
    window.dispatchEvent(new CustomEvent('resume-ai-limit-exceeded'));
    throw new Error('LIMIT_EXCEEDED');
  }
  if (!response.ok) {
    throw new Error(`API_ERROR: ${response.statusText}`);
  }

  return await response.json();
}

// Rotating advanced loading messages per operation type
export const AI_LOADING_MESSAGES = {
  summary: [
    'Analyzing your QA profile…',
    'Optimizing for ATS compatibility…',
    'Generating recruiter-friendly content…',
    'Crafting senior-level language…',
    'Tailoring to your specialization…',
  ],
  bullet: [
    'Enhancing with STAR format…',
    'Adding quantified impact…',
    'Upgrading action verbs…',
    'Optimizing for ATS keywords…',
  ],
  jd: [
    'Parsing job requirements…',
    'Matching your skills to the JD…',
    'Identifying keyword gaps…',
    'Calculating ATS match score…',
  ],
  skills: [
    'Fetching 2026 in-demand skills…',
    'Analyzing role requirements…',
    'Curating top QA keywords…',
  ],
};

export function getLoadingMessage(type) {
  const msgs = AI_LOADING_MESSAGES[type] || AI_LOADING_MESSAGES.summary;
  return msgs[Math.floor(Math.random() * msgs.length)];
}

export function useResumeAI() {
  const [loading, setLoading] = useState({});

  const setLoad = (key, val) => setLoading(prev => ({ ...prev, [key]: val }));

  // 1. Generate professional summary with variations (with serverless Gemini + fallback)
  const generateSummary = useCallback(async ({ specialization, yearsExp, formData }) => {
    setLoad('summary', true);

    const profile = {
      yearsExperience: parseInt(yearsExp) || 3,
      specialization: specialization || 'automation',
      skills: Object.values(formData?.technicalSkills || {}).flat(),
      certifications: formData?.certifications?.map(c => c.name).filter(Boolean) || [],
      achievements: formData?.experience?.flatMap(e => 
        e.achievements?.map(a => ({ 
          text: (typeof a === 'object' && a !== null) ? (a.text || '') : (a || '') 
        })) || []
      ) || [],
    };

    try {
      const res = await callResumeAI('generate-summary', {
        specialization: profile.specialization,
        yearsExp: profile.yearsExperience,
        skills: profile.skills,
        certifications: profile.certifications,
        achievements: profile.achievements.map(a => a.text)
      });
      if (res?.summary) {
        setLoad('summary', false);
        return res.summary;
      }
    } catch (err) {
      console.warn('[useResumeAI] Summary API failed, using client-side rules fallback:', err.message);
    }

    await simulateDelay(600);
    const variations = generateSummaryVariations(profile, {
      yearsExperience: profile.yearsExperience,
      specialization: profile.specialization,
      skills: profile.skills,
      certifications: profile.certifications,
      achievements: profile.achievements,
    });
    
    setLoad('summary', false);
    return variations[0].text; // Return first variation by default
  }, []);

  // 2. Generate summary variations (with tone - serverless Gemini + fallback)
  const generateSummaryWithVariations = useCallback(async ({ specialization, yearsExp, formData, jobDescription = '', tone = 'balanced' }) => {
    setLoad('summaryVariations', true);
    
    const profile = {
      yearsExperience: parseInt(yearsExp) || 3,
      specialization: specialization || 'automation',
      skills: Object.values(formData?.technicalSkills || {}).flat(),
      certifications: formData?.certifications?.map(c => c.name).filter(Boolean) || [],
      achievements: formData?.experience?.flatMap(e => 
        e.achievements?.map(a => ({ 
          text: (typeof a === 'object' && a !== null) ? (a.text || '') : (a || '') 
        })) || []
      ) || [],
    };

    try {
      const res = await callResumeAI('generate-summary', {
        specialization: profile.specialization,
        yearsExp: profile.yearsExperience,
        skills: profile.skills,
        certifications: profile.certifications,
        achievements: profile.achievements.map(a => a.text),
        jobDescription,
        tone
      });
      if (res?.summary) {
        setLoad('summaryVariations', false);
        return [{ tone, text: res.summary }];
      }
    } catch (err) {
      console.warn('[useResumeAI] Summary variations API failed, using client-side rules fallback:', err.message);
    }

    await simulateDelay(600);
    const variations = generateSummaryVariations(profile, {
      yearsExperience: profile.yearsExperience,
      specialization: profile.specialization,
      skills: profile.skills,
      certifications: profile.certifications,
      achievements: profile.achievements,
      jobDescription,
      tone,
    });
    
    setLoad('summaryVariations', false);
    return variations;
  }, []);

  // 3. Enhance a single bullet point (serverless Gemini + fallback)
  const enhanceBullet = useCallback(async ({ bullet, role, specialization = 'automation' }) => {
    if (!bullet?.trim()) return bullet;
    const bulletKey = `bullet_${bullet.slice(0, 20)}`;
    setLoad(bulletKey, true);

    try {
      const res = await callResumeAI('enhance-bullet', {
        bullet,
        role,
        specialization
      });
      if (res?.enhancedBullet) {
        setLoad(bulletKey, false);
        return res.enhancedBullet;
      }
    } catch (err) {
      console.warn('[useResumeAI] Enhance bullet API failed, using client-side rules fallback:', err.message);
    }

    await simulateDelay(400);
    const enhanced = enhanceBulletAI(bullet, {
      specialization,
      addMetrics: true,
      style: 'balanced',
      variation: Math.floor(Math.random() * 3),
    });

    setLoad(bulletKey, false);
    return enhanced;
  }, []);

  // 3b. Generate a new STAR bullet point with variations
  const generateSTARBullet = useCallback(async ({ situation, task, action, result, role, specialization = 'automation' }) => {
    setLoad('starBullet', true);
    try {
      const res = await callResumeAI('generate-star-bullet', {
        situation, task, action, result, role, specialization
      });
      if (res?.variations) {
        setLoad('starBullet', false);
        return res;
      }
    } catch (err) {
      if (err.message === 'LIMIT_EXCEEDED') {
        setLoad('starBullet', false);
        throw err;
      }
      console.warn('[useResumeAI] Generate STAR bullet API failed, using client-side fallback:', err.message);
    }
    
    // Fallback if API fails (offline, rate limit, etc.)
    await simulateDelay(500);
    const fallback = generateSTARBulletFallback({ situation, task, action, result, role, specialization });
    setLoad('starBullet', false);
    return fallback;
  }, []);

  // 4. Suggest skills for a specialization — returns full structured recommendations
  const suggestSkillsForRole = useCallback(async (specialization) => {
    setLoad('skills', true);
    await simulateDelay(700);

    const spec = QA_SPECIALIZATIONS[specialization];
    if (!spec) { setLoad('skills', false); return null; }

    setLoad('skills', false);
    return {
      specId:          spec.id,
      specLabel:       spec.label,
      specIcon:        spec.icon,
      skills:          spec.suggestedSkills  || {},
      tools:           spec.suggestedTools   || {},
      certifications:  spec.certifications   || [],
      summaryKeywords: spec.summaryKeywords  || [],
      bulletStarters:  spec.bulletStarters   || [],
    };
  }, []);

  // 5. Analyze job description — passes full profile for accurate matching
  const analyzeJobDescription = useCallback(async (jdText, currentProfile = {}) => {
    if (!jdText?.trim()) return null;
    setLoad('jd', true);
    try {
      const res = await callResumeAI('analyze-jd', {
        jdText,
        profile: {
          skills: currentProfile.skills || [],
          tools: currentProfile.tools || [],
          certifications: currentProfile.certifications || [],
          yearsExperience: currentProfile.yearsExperience || 0,
        }
      });
      if (res?.analysis) {
        setLoad('jd', false);
        return cleanAndEnhanceAnalysis(res.analysis);
      }
    } catch (err) {
      console.warn('[useResumeAI] analyzeJobDescription API failed, using client-side fallback:', err.message);
    }

    await simulateDelay(600);
    const analysis = analyzeJD(jdText, currentProfile);
    setLoad('jd', false);
    return analysis;
  }, []);

  // 5b. Analyze JD with full formData context (preferred call path)
  const analyzeJobDescriptionWithProfile = useCallback(async (jdText, formData) => {
    if (!jdText?.trim()) return null;
    setLoad('jd', true);
    const profile = {
      skills:         Object.values(formData?.technicalSkills || {}).flat(),
      tools:          Object.values(formData?.tools || {}).flat(),
      certifications: (formData?.certifications || []).map(c => c.name).filter(Boolean),
      yearsExperience: 0,
    };

    try {
      const res = await callResumeAI('analyze-jd', {
        jdText,
        profile
      });
      if (res?.analysis) {
        setLoad('jd', false);
        return cleanAndEnhanceAnalysis(res.analysis);
      }
    } catch (err) {
      console.warn('[useResumeAI] analyzeJobDescriptionWithProfile API failed, using client-side fallback:', err.message);
    }

    await simulateDelay(600);
    const analysis = analyzeJD(jdText, profile);
    setLoad('jd', false);
    return analysis;
  }, []);

  // 6. Get ATS keyword gap suggestions from current formData
  const getAtsKeywordSuggestions = useCallback((formData) => {
    const allSelected = [
      ...Object.values(formData?.technicalSkills || {}).flat(),
      ...Object.values(formData?.tools || {}).flat(),
      ...formData?.coreCompetencies || [],
    ].map(s => s.toLowerCase());

    // Get suggestions from all specializations
    const allKeywords = Object.values(QA_SPECIALIZATIONS)
      .flatMap(spec => [...spec.keywords, ...spec.tools]);
    
    return [...new Set(allKeywords)]
      .filter(kw => !allSelected.includes(kw.toLowerCase()))
      .slice(0, 8);
  }, []);

  return {
    loading,
    generateSummary,
    generateSummaryWithVariations,
    enhanceBullet,
    generateSTARBullet,
    suggestSkillsForRole,
    analyzeJobDescription,
    analyzeJobDescriptionWithProfile,
    getAtsKeywordSuggestions,
    specializations: Object.values(QA_SPECIALIZATIONS).map(spec => ({
      id: spec.id,
      label: spec.label,
      icon: spec.keywords[0]?.charAt(0) || '🎯',
      desc: spec.keywords.slice(0, 3).join(', '),
    })),
  };
}
